import { z } from 'zod';

/**
 * The capabilities a plan can unlock — the catalog `EntitlementService.can()`
 * accepts and the tier matrix is drawn from.
 *
 * ## Why it is a closed list
 *
 * `billing.tierCapabilities` maps a tier to the capabilities it opens, and P26
 * edits that as a **tier × capability matrix** — tiers down one axis, this list
 * across the other, checkboxes in the cells — precisely so no operator ever
 * types a capability key (U0). A free string would make the matrix
 * unbuildable and a typo indistinguishable from a denial.
 *
 * ## Why exactly these three, today
 *
 * They are the surfaces that actually gate: browsing members, expressing
 * interest, and conversing. Each was built ungated on purpose, with the reason
 * written into its own service, because N14 allows commercial gating only here.
 * A capability is added by the feature that gates on it — never speculatively,
 * because an unreferenced key is a checkbox in the admin matrix that does
 * nothing.
 *
 * P26's candidate tier levers (see-who-liked-me, incognito, boost, advanced
 * filters) join this list **with the features that read them**, not before.
 */
export const BILLING_CAPABILITIES = ['browse', 'likes', 'chat'] as const;

export const BillingCapabilitySchema = z.enum(BILLING_CAPABILITIES);

export type BillingCapability = (typeof BILLING_CAPABILITIES)[number];

const CAPABILITY_SET: ReadonlySet<string> = new Set(BILLING_CAPABILITIES);

/**
 * Narrows an operator-supplied string from `billing.tierCapabilities`, whose
 * schema is `Record<string, string[]>` because tiers are a runtime concept.
 * An unknown capability in the map is **ignored**, never an error: a tier that
 * still names a capability the code has since dropped must not break gating for
 * every member on it.
 */
export function isBillingCapability(value: string): value is BillingCapability {
  return CAPABILITY_SET.has(value);
}
